import React, { useState, useEffect } from "react";
import { db } from "../../../firebase/config";
import { collection, query, where, getDocs, orderBy, doc, updateDoc } from "firebase/firestore";

export default function Portal() {
  const [portal, setPortal] = useState(null);
  const [turnos, setTurnos] = useState([]);
  const [clinicaData, setClinicaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('proximos');

  const token = new URLSearchParams(window.location.search).get('token');
  const [confirmando, setConfirmando] = useState(null);

async function responderTurno(turnoId, respuesta) {
    setConfirmando(turnoId);
    try {
      const estadoAgenda = respuesta === 'confirmado' ? 'confirmado' : 'cancelado';
      await updateDoc(doc(db, 'turnos', turnoId), {
        estadoPaciente: respuesta,
        estado: estadoAgenda,
        fechaRespuestaPaciente: new Date(),
      });
      setTurnos(prev => prev.map(t => t.id === turnoId ? { ...t, estadoPaciente: respuesta, estado: estadoAgenda } : t));
    } catch (e) { console.error(e); }
    setConfirmando(null);
  }

  useEffect(() => {
    async function cargarPortal() {
      if (!token) { setError('Link inválido'); setLoading(false); return; }

      const q = query(collection(db, 'portales'), where('token', '==', token));
      const snap = await getDocs(q);

      if (snap.empty) { setError('Link no encontrado'); setLoading(false); return; }

      const portalData = { id: snap.docs[0].id, ...snap.docs[0].data() };
      const vencimiento = portalData.vencimiento?.toDate ? portalData.vencimiento.toDate() : new Date(portalData.vencimiento);

      if (vencimiento < new Date()) { setError('Este link ha vencido. Solicitá uno nuevo en la clínica.'); setLoading(false); return; }

      setPortal(portalData);

      if (portalData.clinicaId) {
        const { doc: docFn, getDoc } = await import('firebase/firestore');
        const clinicaSnap = await getDoc(docFn(db, 'clinicas', portalData.clinicaId));
        if (clinicaSnap.exists()) setClinicaData(clinicaSnap.data());
      }

      const qTurnos = query(collection(db, 'turnos'), where('dni', '==', portalData.dni), where('clinicaId', '==', portalData.clinicaId), orderBy('fecha', 'desc'));
      const snapTurnos = await getDocs(qTurnos);
      setTurnos(snapTurnos.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    cargarPortal();
  }, [token]);

  function formatFechaHora(timestamp) {
    if (!timestamp?.toDate) return '—';
    return timestamp.toDate().toLocaleString('es-AR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function getTipoBadge(tipo) {
    const tipos = {
      consulta: { label: 'Consulta', bg: '#E6F1FB', color: '#0C447C' },
      control: { label: 'Control', bg: '#E1F5EE', color: '#085041' },
      estudio: { label: 'Estudio', bg: '#FAEEDA', color: '#633806' },
      cirugia: { label: 'Cirugía', bg: '#FAECE7', color: '#712B13' },
    };
    return tipos[tipo] || tipos.consulta;
  }

  function getMedicoNombre(medico) {
    if (!clinicaData?.medicos) return medico === 'medico1' ? 'Médico 1' : 'Médico 2';
    return clinicaData.medicos[medico] || medico;
  }

  function descargarReceta(turno) {
    const nombreClinica = clinicaData?.nombre || 'Su clínica';
    const direccion = clinicaData?.direccion ? `${clinicaData.direccion}, ${clinicaData.ciudad || ''}` : '';
    const ventana = window.open('', '_blank');
    ventana.document.write(`
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #1a1a1a; font-size: 13px; }
          .header { border-bottom: 2px solid #223A5E; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-start; }
          .titulo { font-size: 15px; font-weight: bold; color: #223A5E; margin: 20px 0 16px; }
          .dato { margin-bottom: 6px; }
          .dato-label { font-size: 10px; color: #888; text-transform: uppercase; }
          .dato-value { font-size: 13px; font-weight: 500; }
          .receta-box { border: 1px solid #ddd; padding: 20px; border-radius: 4px; margin: 20px 0; line-height: 1.8; min-height: 120px; }
          .footer { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 12px; font-size: 10px; color: #888; display: flex; justify-content: space-between; }
          .firma { text-align: center; margin-top: 60px; }
          .firma-linea { border-top: 1px solid #333; padding-top: 8px; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div style="font-size: 20px; font-weight: 700; color: #223A5E;">${nombreClinica}</div>
            ${direccion ? `<div style="font-size:11px; color:#666; margin-top:4px;">${direccion}</div>` : ''}
          </div>
          <div style="text-align:right; font-size:12px; color:#444;">
            <div style="font-weight:600;">${getMedicoNombre(turno.medico)}</div>
          </div>
        </div>
        <div class="titulo">Receta Médica</div>
        <div style="margin-bottom:20px;">
          <div class="dato"><div class="dato-label">Paciente</div><div class="dato-value">${portal?.nombre || turno.paciente}</div></div>
          <div class="dato"><div class="dato-label">DNI</div><div class="dato-value">${portal?.dni || turno.dni}</div></div>
          <div class="dato"><div class="dato-label">Fecha</div><div class="dato-value">${new Date().toLocaleDateString('es-AR')}</div></div>
        </div>
        <div class="receta-box">${turno.receta?.replace(/\n/g, '<br/>') || ''}</div>
        <div class="firma">
          <div style="height:50px;"></div>
          <div class="firma-linea">${getMedicoNombre(turno.medico)}</div>
        </div>
        <div class="footer">
          <span>${nombreClinica}${direccion ? ` · ${direccion}` : ''}</span>
          <span>Fecha de emisión: ${new Date().toLocaleDateString('es-AR')}</span>
        </div>
      </body>
      </html>
    `);
    ventana.document.close();
    ventana.print();
  }

  const ahora = new Date();
  const turnosProximos = turnos.filter(t => { const f = t.fecha?.toDate ? t.fecha.toDate() : null; return f && f >= ahora && t.estado !== 'cancelado'; }).sort((a, b) => a.fecha?.toDate?.() - b.fecha?.toDate?.());
  const turnosPasados = turnos.filter(t => { const f = t.fecha?.toDate ? t.fecha.toDate() : null; return f && f < ahora; });

  const nombreClinica = clinicaData?.nombre || 'Su clínica';
  const primaryColor = '#223A5E';

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ textAlign: 'center', color: '#8A9BB5' }}>Cargando tu portal...</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ textAlign: 'center', maxWidth: '400px', padding: '32px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <div style={{ fontSize: '16px', fontWeight: '600', color: primaryColor, marginBottom: '8px' }}>Acceso no disponible</div>
        <div style={{ fontSize: '13px', color: '#8A9BB5', marginBottom: '16px' }}>{error}</div>
        <div style={{ fontSize: '12px', color: '#8A9BB5' }}>Comunicarte con la clínica para obtener un nuevo acceso.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F4F7FB', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ background: primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
        <div style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '700' }}>{nombreClinica}</div>
        <div style={{ color: '#8A9BB5', fontSize: '12px' }}>Portal del paciente</div>
      </div>

      <div style={{ padding: '24px', maxWidth: '700px', margin: '0 auto' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '0.5px solid #D0D8E8', padding: '16px 20px', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: primaryColor, marginBottom: '4px' }}>Hola, {portal?.nombre}</div>
          <div style={{ fontSize: '12px', color: '#8A9BB5' }}>Aquí podés consultar tus turnos, indicaciones y recetas en {nombreClinica}.</div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {[{ id: 'proximos', label: `Próximos turnos (${turnosProximos.length})` }, { id: 'pasados', label: `Historial (${turnosPasados.length})` }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '8px', cursor: 'pointer', border: tab === t.id ? 'none' : '0.5px solid #D0D8E8', background: tab === t.id ? primaryColor : '#FFFFFF', color: tab === t.id ? '#FFFFFF' : '#8A9BB5', fontFamily: "'Montserrat', sans-serif", fontWeight: tab === t.id ? '600' : '400' }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'proximos' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {turnosProximos.length === 0
              ? <div style={{ textAlign: 'center', color: '#8A9BB5', padding: '48px', fontSize: '13px', background: '#FFFFFF', borderRadius: '12px', border: '0.5px solid #D0D8E8' }}>No tenés turnos próximos programados</div>
              : turnosProximos.map(t => {
                  const tipo = getTipoBadge(t.tipo);
                  return (
                    <div key={t.id} style={{ background: '#FFFFFF', borderRadius: '12px', border: '0.5px solid #D0D8E8', overflow: 'hidden' }}>
                      <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #D0D8E8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: primaryColor, marginBottom: '2px' }}>{formatFechaHora(t.fecha)}</div>
                          <div style={{ fontSize: '12px', color: '#8A9BB5' }}>{getMedicoNombre(t.medico)}</div>
                        </div>
                        <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '99px', background: tipo.bg, color: tipo.color, fontWeight: '500' }}>{tipo.label}</span>
                      </div>
                      {(t.indicacionesPre || t.detalle) && (
                        <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #D0D8E8' }}>
                          {t.detalle && <div style={{ marginBottom: '8px' }}><div style={{ fontSize: '11px', color: '#8A9BB5', marginBottom: '2px', fontWeight: '500' }}>PROCEDIMIENTO</div><div style={{ fontSize: '13px', color: primaryColor }}>{t.detalle}</div></div>}
                          {t.indicacionesPre && <div><div style={{ fontSize: '11px', color: '#8A9BB5', marginBottom: '2px', fontWeight: '500' }}>INDICACIONES PREVIAS</div><div style={{ fontSize: '13px', color: primaryColor, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{t.indicacionesPre}</div></div>}
                        </div>
                      )}
                      <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {t.estadoPaciente === 'confirmado' && (
                          <span style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '99px', background: '#E1F5EE', color: '#085041', fontWeight: '600' }}>✓ Turno confirmado</span>
                        )}
                        {t.estadoPaciente === 'rechazado' && (
                          <span style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '99px', background: '#FAECE7', color: '#712B13', fontWeight: '600' }}>✕ Turno rechazado</span>
                        )}
                        {!t.estadoPaciente && (
                          <>
                            <button onClick={() => responderTurno(t.id, 'confirmado')} disabled={confirmando === t.id} style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', border: 'none', cursor: 'pointer', background: '#085041', color: '#FFFFFF', fontFamily: "'Montserrat', sans-serif" }}>
                              {confirmando === t.id ? '...' : '✓ Confirmar turno'}
                            </button>
                            <button onClick={() => responderTurno(t.id, 'rechazado')} disabled={confirmando === t.id} style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600', borderRadius: '8px', border: '0.5px solid #712B13', cursor: 'pointer', background: 'transparent', color: '#712B13', fontFamily: "'Montserrat', sans-serif" }}>
                              {confirmando === t.id ? '...' : '✕ Rechazar turno'}
                            </button>
                            <span style={{ fontSize: '11px', color: '#8A9BB5' }}>¿Podés asistir?</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
            }
          </div>
        )}

        {tab === 'pasados' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {turnosPasados.length === 0
              ? <div style={{ textAlign: 'center', color: '#8A9BB5', padding: '48px', fontSize: '13px', background: '#FFFFFF', borderRadius: '12px', border: '0.5px solid #D0D8E8' }}>No hay turnos anteriores registrados</div>
              : turnosPasados.map(t => {
                  const tipo = getTipoBadge(t.tipo);
                  return (
                    <div key={t.id} style={{ background: '#FFFFFF', borderRadius: '12px', border: '0.5px solid #D0D8E8', overflow: 'hidden' }}>
                      <div style={{ padding: '14px 16px', borderBottom: (t.indicacionesPost || t.receta) ? '0.5px solid #D0D8E8' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: primaryColor, marginBottom: '2px' }}>{formatFechaHora(t.fecha)}</div>
                          <div style={{ fontSize: '12px', color: '#8A9BB5' }}>{getMedicoNombre(t.medico)}</div>
                        </div>
                        <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '99px', background: tipo.bg, color: tipo.color, fontWeight: '500' }}>{tipo.label}</span>
                      </div>
                      {(t.indicacionesPost || t.receta) && (
                        <div style={{ padding: '12px 16px' }}>
                          {t.indicacionesPost && <div style={{ marginBottom: t.receta ? '12px' : '0' }}><div style={{ fontSize: '11px', color: '#8A9BB5', marginBottom: '2px', fontWeight: '500' }}>INDICACIONES POST TURNO</div><div style={{ fontSize: '13px', color: primaryColor, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{t.indicacionesPost}</div></div>}
                          {t.receta && (
                            <div>
                              <div style={{ fontSize: '11px', color: '#8A9BB5', marginBottom: '8px', fontWeight: '500' }}>RECETA MÉDICA</div>
                              <div style={{ background: '#F4F7FB', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', color: primaryColor, marginBottom: '10px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{t.receta}</div>
                              <button onClick={() => descargarReceta(t)} style={{ background: primaryColor, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" }}>
                                Descargar receta PDF
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
            }
          </div>
        )}
      </div>
    </div>
  );
}